import React from 'react';
import { Alert, AlertDescription, AlertIcon } from '@chakra-ui/alert';
import { Box, Center, Text, useColorModeValue } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import { Notification } from 'clipsal-cortex-types/src/api/api-notifications';

import { NotificationListItem } from './NotificationListItem';
import { useNotificationSearchMatcher } from './notifications-helper';
import { NotificationGrouping, useGetNotifications } from './notificationsApi';
import { NotificationsListSkeletonLoader } from './NotificationsListSkeletonLoader';

type Props = {
  groupBy: NotificationGrouping;
  // The query limit — grows by a page each time "Load more" is clicked
  limit?: number;
  // Only notifications whose title matches this search term are fetched
  searchQuery?: string;
  isArchived?: boolean;
  isEditMode?: boolean;
  selectedIds?: Set<string>;
  onToggleSelect?: (notificationId: string) => void;
  onSwipeSelect?: (notificationId: string) => void;
};

export function NotificationsListReadStatus({
  groupBy,
  limit = 10,
  searchQuery = '',
  isArchived = false,
  isEditMode = false,
  selectedIds,
  onToggleSelect,
  onSwipeSelect,
}: Props) {
  const textColor = useColorModeValue('#111111', '#EEEEEE');
  const {
    data: { read, unread },
    isLoading,
    isFetching,
    isError,
  } = useGetNotifications(groupBy, { limit, offset: 0, archived: isArchived, search: searchQuery || undefined });
  const { t } = useTranslation();
  const matchesSearch = useNotificationSearchMatcher(searchQuery);
  const fetchedUnreadNotifications = unread?.data?.results;
  // Filter by title client-side as well, so search works even if the API ignores the `search` param
  const unreadNotifications = fetchedUnreadNotifications?.filter(matchesSearch);
  const totalNumUnreadNotifications = unread?.data?.count ?? 0;
  const fetchedReadNotifications = read?.data?.results;
  const readNotifications = fetchedReadNotifications?.filter(matchesSearch);
  const totalNumReadNotifications = read?.data?.count ?? 0;

  // True while a larger page is in flight after growing the limit (but not during background polling)
  function isFetchingMore(notifications: Notification[] | undefined, totalCount: number) {
    return isFetching && !!notifications && notifications.length < limit && notifications.length < totalCount;
  }

  function toListItems(notifications: Notification[] | undefined) {
    return isLoading ? (
      <NotificationsListSkeletonLoader />
    ) : (
      notifications?.map((notification) => (
        <NotificationListItem
          notification={notification}
          key={notification.notification_id}
          isArchived={isArchived}
          isEditMode={isEditMode}
          isSelected={selectedIds?.has(notification.notification_id)}
          onToggleSelect={onToggleSelect}
          onSwipeSelect={onSwipeSelect}
        />
      ))
    );
  }

  if (isError) {
    return (
      <Alert status="error" my={4}>
        <AlertIcon w={8} h={8} />
        <AlertDescription py={4}>
          {t('Notifications.error fetching notifications')}.{' '}
          {`${t('Common.please try again')} ${t('Common.if this persists contact support')}`}.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      <Text data-testid="list-type-title-unread" color={textColor}>
        {t('Notifications.unread').toUpperCase()}
      </Text>
      <Box data-testid="notifications-list-unread" mb={3}>
        {!unreadNotifications?.length && !isLoading ? (
          <Center data-testid="no-unread-notifications" my={4}>
            <Text>{t('Notifications.no type notifications', { type: 'unread' })}.</Text>
          </Center>
        ) : (
          toListItems(unreadNotifications)
        )}

        {isFetchingMore(fetchedUnreadNotifications, totalNumUnreadNotifications) && (
          <NotificationsListSkeletonLoader numItems={2} />
        )}
      </Box>
      <Text data-testid="list-type-title-read" color={textColor}>
        {t('Notifications.read').toUpperCase()}
      </Text>
      <Box data-testid="notifications-list-read" mb={3}>
        {!readNotifications?.length && !isLoading ? (
          <Center data-testid="no-read-notifications" my={4}>
            <Text>{t('Notifications.no type notifications', { type: 'read' })}.</Text>
          </Center>
        ) : (
          toListItems(readNotifications)
        )}

        {isFetchingMore(fetchedReadNotifications, totalNumReadNotifications) && (
          <NotificationsListSkeletonLoader numItems={2} />
        )}
      </Box>
    </Box>
  );
}
